import React, { useState, useEffect} from 'react';
import { string, object, bool, oneOfType, func } from 'prop-types';
import RangeChart from 'ps-components/RangeChartEnhanced/RangeChartEnhanced';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import classnames from 'classnames';
import { roundValue, getMonetaryDivisor } from 'ps-components/lib/formatters';
import './RangeDropdown.scss';

RangeDropdown.propTypes = {
  className: string,
  rangeChartData: object,
  primaryClassName: string,
  secondaryClassName: string,
  containerClassName: string,
  primaryText: oneOfType([string, object]),
  secondaryText: oneOfType([string, object]),
  rangeChartLoading: bool,
  rangeChartLoaded: bool,
  rangeChartError: bool,
  displayIn: string,
  closeRangeChart: func,
  genderToggle: bool
};

// Thanks stack overflow
// https://stackoverflow.com/questions/10599933/convert-long-number-into-abbreviated-string-in-javascript-with-a-special-shortn/32638472
function formatAnnual(value) {
  var suffixes = ['', 'k', 'm', 'b', 't'];
  var suffixNum = Math.floor(('' + Math.round(value)).length / 4);
  var shortValue = Math.round(parseFloat((suffixNum != 0 ? value / Math.pow(1000, suffixNum) : value).toPrecision(3)));
  if (shortValue % 1 != 0) {
    shortValue = shortValue.toFixed(1);
  }
  return shortValue + suffixes[suffixNum];
}

function formatHourly(value) {
  return roundValue(value / 2080, 2);
}

function formatPercentOf(value, compare) {
  return roundValue(100 * (value / compare), 1) + '%';
}

const dropdownOptions = {
  'Base Salary': 'base',
  'Total Cash': 'tc',
  'Total Target Cash': 'ttc',
  'Total Direct': 'td',
  'Total Guaranteed Pay': 'tgp'
};

const targetReplacementKeys = {
  '62_5th': '62.5th',
  '60th_interp': 'interpreted 60th',
  '65th_interp': 'interpreted 65th'
};

const hoverFillColor = 'rgb(15, 178, 246)'; // #0FB2F6 / rgb(15, 178, 246) / $skyblue
const clickFillColor = 'rgb(8, 145, 202)'; //#0891CA / rgb(8, 145, 202) / $skyblue-dark

// Original fill colors for both genders
const originalFillMaleColor = 'rgb(15, 178, 246)';
const originalFillFemaleColor = 'rgb(198, 179, 252)';
const originalFillNullColor = 'rgb(200, 200, 200)';
//Original stroke colors for genders
const originalStrokeMaleColor = 'rgb(2, 132, 187)';
const originalStrokeFemaleColor = 'rgb(148, 131, 194)';
const originalStrokeNullColor = 'rgb(180, 180, 180)';
// Lighter colors for gender-specific hover fill colors
const hoverFillFemaleFillColor =  'rgb(239, 215, 255)'; 
const hoverFillMaleFillColor = 'rgb(191, 230, 255)'; 
const hoverFillNullColor = 'rgb(230, 230, 230)'; 
//Click color fill and stroke
const clickFillColorGender = 'rgb(241, 166, 40)';
const clickStrokeColorGender = 'rgb(196, 137, 39)';


export default function RangeDropdown(props) {
  const [selectedDataSet, setSelectedDataSet] = useState('base');
  const [dropdownText, setDropdownText] = useState('Base Salary');
  const [employeeData, setEmployeeData] = useState(null);
  const [clickedEmployeeData, setClickedEmployeeData] = useState(null);
  const rangeData = props.rangeChartData[selectedDataSet]?.data,
    scatterData = props.rangeChartData[selectedDataSet]?.scatterData,
    notEnoughValues = rangeData && Object.values(rangeData).length < 2,
    incompleteData = !(rangeData && Object.keys(rangeData)?.some(value => rangeData[value])) || notEnoughValues,
    noDataText = notEnoughValues ? 'Not enough data available' : 'No data',
    targetData = props.rangeChartData.target && props.rangeChartData.target[selectedDataSet];
  
  useEffect(() => {
      setEmployeeData(null);
      setClickedEmployeeData(null);
    }, [props.genderToggle]);


  const renderedEmployeeData = clickedEmployeeData || employeeData;
  const clickDotDataWithGenderToggle = clickedEmployeeData && props.genderToggle;

  function formatCurrency(value) {
    const divisor = getMonetaryDivisor(props.displayIn);
    let decimal = 0;
    if (props.displayIn === 'hourly') {
      decimal = 2;
    } else if (props.displayIn === 'thousands') {
      decimal = 1;
    }
    return roundValue(value / divisor, decimal);
  }

  function handleDropdownChange(value) {
    setSelectedDataSet(dropdownOptions[value]);
    setEmployeeData(null);
    setClickedEmployeeData(null);
    setDropdownText(value);
  }

  function handleEmployeeHover(value, point, genderToggle) {
    if (genderToggle) {
      const currentFillColor = point.style('fill');
      if (currentFillColor !== clickFillColorGender) {
        let hoverFill;
        if (currentFillColor === originalFillFemaleColor) {
          hoverFill = hoverFillFemaleFillColor;
        } else if (currentFillColor === originalFillMaleColor) {
          hoverFill = hoverFillMaleFillColor;
        } else {
          hoverFill = hoverFillNullColor;
        }
        point.style({ fill: hoverFill, stroke: hoverFill});
      }
    } 
    else {
      // Original code for hover without gender toggle
      if (point.style('fill') !== clickFillColor) {
        point.style({ fill: hoverFillColor, stroke: hoverFillColor });
      }
    }

    setEmployeeData(value);
    
  }
    
  function handleEmployeeHoverOut(value, point, genderToggle) {
    if (genderToggle) {
      const currentFillColor = point.style('fill');
      if (currentFillColor !== clickFillColorGender && currentFillColor != originalFillMaleColor 
      && currentFillColor != originalFillFemaleColor && currentFillColor != originalFillNullColor) {
        let originalFillColor;
        let originalStrokeColor;
        if (currentFillColor === hoverFillFemaleFillColor) {
          originalFillColor = originalFillFemaleColor;
          originalStrokeColor = originalStrokeFemaleColor;
        } else if (currentFillColor === hoverFillMaleFillColor) {
          originalFillColor = originalFillMaleColor;
          originalStrokeColor = originalStrokeMaleColor;
        } else {
          originalFillColor = originalFillNullColor;
          originalStrokeColor = originalStrokeNullColor;
        }
        point.style({ fill: originalFillColor, stroke: originalStrokeColor });
      }
    } 
    else {
      // Original code for hover out without gender toggle
      if (point.style('fill') !== clickFillColor) {
        point.style({ fill: '#FFFFFF', stroke: '#000000' });
      }
    }

    setEmployeeData(null);
  }

  function handleEmployeeClick(value, point, scatterPoints, genderToggle) {
    if (genderToggle) {
      const currentFillColor = point.style('fill');
      if (currentFillColor === clickFillColorGender) {
        const gender = point[0][0].__data__.gender;
        let originalFillColor, originalStrokeColor;
        if (gender === 'F' || gender === 'Female') {
          originalFillColor = originalFillFemaleColor;
          originalStrokeColor = originalStrokeFemaleColor;
        } else if (gender === 'M' || gender === 'Male') {
          originalFillColor = originalFillMaleColor;
          originalStrokeColor = originalStrokeMaleColor;
        } else {
          originalFillColor = originalFillNullColor;
          originalStrokeColor = originalStrokeNullColor;
        }
        point.style({ fill: originalFillColor, stroke: originalStrokeColor });
        setClickedEmployeeData(null);
      } 
      else {
        scatterPoints.style({
          stroke: (d) => {
            if (d.gender === 'F' || d.gender === 'Female') return originalStrokeFemaleColor;
            if (d.gender === 'M' || d.gender === 'Male') return originalStrokeMaleColor;
            return originalStrokeNullColor;
          },
          'stroke-width': 1,
          fill: (d) => {
            if (d.gender === 'F' || d.gender === 'Female') return originalFillFemaleColor;
            if (d.gender === 'M' || d.gender === 'Male') return originalFillMaleColor;
            return originalFillNullColor; // for null or unspecified gender
          }
        });
        point.style({ fill: clickFillColorGender, stroke: clickStrokeColorGender});
        setClickedEmployeeData(value);
      }
    } 
    else {
      // Original code for click without gender toggle
      if (point.style('fill') === clickFillColor) {
        point.style({ fill: '#FFFFFF', stroke: '#000000' });
        setClickedEmployeeData(null);
      } else {
        scatterPoints.style({ fill: '#FFFFFF', stroke: '#000000' });
        point.style({ fill: clickFillColor, stroke: clickFillColor });
        setClickedEmployeeData(value);
      }

    }
  }
    

  return (
    <div className={classnames('pricing-details__range-dropdown', props.className)}>
      <div className="pricing-details__range-dropdown-close">
        <i onClick={props.closeRangeChart} className="close-button icon-cancel" />
      </div>
      <div className="pricing-details__range-dropdown-container">
        <div className="pricing-details__range-dropdown-select">
          <label>Show</label>
          <Dropdown
            text={dropdownText}
            dropdownItems={Object.keys(dropdownOptions)}
            itemOnClick={handleDropdownChange}
          />
        </div>
        <RangeChart
          data={rangeData}
          scatterData={scatterData}
          incompleteData={!props.rangeChartLoading && (incompleteData || props.rangeChartError)}
          id="pricing-details__range-dropdown"
          reportMode="print"
          currencyFormatter={props.displayIn === 'hourly' ? formatHourly : formatAnnual}
          animate={true}
          toolTipWidth={155}
          width={500}
          loading={props.rangeChartLoading}
          scatterHoverHandler={handleEmployeeHover}
          scatterHoverHandlerOut={handleEmployeeHoverOut}
          scatterClickHandler={handleEmployeeClick}
          noDataText={noDataText}
          genderToggle={props.genderToggle}
        />
      </div>
      {scatterData?.length > 0 && !incompleteData && !renderedEmployeeData && (
        <div className="pricing-details__range-dropdown-employee-view">
          <div className={`gender-indicators ${props.genderToggle ? 'visible' : 'hidden'}`}>
                <span className="male-indicator-dot"></span>
                <span className="gender-label">Male</span>
                <span className="female-indicator-dot"></span>
                <span className="gender-label">Female</span>
            </div>
            Hover over the dots on the chart to view employee details. <br></br>Click on the dots to make the information
            sticky.
        </div>
      )}
      {renderedEmployeeData && (
        <div className="pricing-details__range-dropdown-employee-details">
          <div className="employee-name">
            <span className="details-label">
                {clickDotDataWithGenderToggle && ( // Conditionally render the dot
                  <span className="orange-dot" style={{ backgroundColor: clickFillColorGender, borderColor: clickStrokeColorGender }}></span>
                )}
                Employee
            </span>
            <span className="details-value">
              {renderedEmployeeData.fullName || 'No name'} <span>{renderedEmployeeData.eeNumber}</span>
            </span>
          </div>
          <div className="employee-monetary">
            <span className="details-label">{dropdownText}</span>
            <span className="details-value">
              {renderedEmployeeData.y ? formatCurrency(renderedEmployeeData.y) : 'No Data'}
            </span>
          </div>
          {rangeData[targetData] && (
            <div className="employee-monetary">
              <span className="details-label">% of Mkt {targetReplacementKeys[targetData] || targetData} (target)</span>
              <span className="details-value">
                {renderedEmployeeData.y ? formatPercentOf(renderedEmployeeData.y, rangeData[targetData]) : 'No Data'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
